import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Business,
  Person,
  Assignment,
  Visibility,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { Organization, User, Activity } from '../api/BackofficeApi';

interface OrganizationDetailsProps {}

const OrganizationDetails: React.FC<OrganizationDetailsProps> = () => {
  const { organizationName } = useParams<{ organizationName: string }>();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'activities'>('users');

  useEffect(() => {
    if (organizationName) {
      fetchOrganizationDetails();
    }
  }, [organizationName]);

  const fetchOrganizationDetails = async () => {
    try {
      setLoading(true);
      
      const [orgDetails, orgUsers, orgActivities] = await Promise.all([
        authAPI.getOrganization(organizationName!),
        authAPI.getUsers(organizationName!),
        authAPI.getActivities({ limit: 100, organization: organizationName })
      ]);
      
      setOrganization(orgDetails);
      setUsers(orgUsers);
      setActivities(orgActivities);
      
    } catch (err: any) {
      setError('Failed to load organization details');
      console.error('Organization details error:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleUserClick = (user: User) => {
    navigate(`/users/${user.organization}/${user.username}/activities`);
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredActivities = activities.filter(activity =>
    activity.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !organization) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organizations')}
        >
          Back to Organizations
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/organizations')}
          sx={{ textDecoration: 'none' }}
        >
          Organizations
        </Link>
        <Typography color="text.primary">{organization.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Business sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">{organization.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Organization Details
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organizations')}
        >
          Back to Organizations
        </Button>
      </Box>

      {/* Organization Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {organization.tokens}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Users
              </Typography>
              <Typography variant="h4">
                {users.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recent Activities
              </Typography>
              <Typography variant="h4">
                {activities.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Permission Groups
              </Typography>
              <Typography variant="h4">
                {organization.permissionsGroup.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Permission Groups */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Permission Groups
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {organization.permissionsGroup.map((group, index) => (
            <Chip
              key={index}
              label={group.name}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>


      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Button
          variant={activeTab === 'users' ? 'contained' : 'text'}
          onClick={() => setActiveTab('users')}
          sx={{ mr: 2 }}
        >
          <Person sx={{ mr: 1 }} />
          Users ({users.length})
        </Button>
        <Button
          variant={activeTab === 'activities' ? 'contained' : 'text'}
          onClick={() => setActiveTab('activities')}
        >
          <Assignment sx={{ mr: 1 }} />
          Activities ({activities.length})
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder={`Search ${activeTab}...`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Users Tab */}
      {activeTab === 'users' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow 
                  key={user.username}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleUserClick(user)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Person sx={{ mr: 1, color: 'text.secondary' }} />
                      <Button
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(user);
                        }}
                        sx={{ 
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          p: 0,
                          minWidth: 'auto',
                          textAlign: 'left',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {user.username}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.authorization ? 'Active' : 'Inactive'}
                      color={user.authorization ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserClick(user);
                      }}
                      title="View User Activities"
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <>
          {filteredActivities.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No activities found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm 
                  ? 'Try adjusting your search terms or clear the search to see all activities'
                  : 'This organization has no recorded activities yet'
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Activity Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivities.map((activity, index) => {
                const getActivityType = (description: string) => {
                  if (!description) return 'Unknown';
                  const desc = description.toLowerCase();
                  if (desc.includes('login') || desc.includes('authentication')) return 'Authentication';
                  if (desc.includes('create') || desc.includes('add')) return 'Creation';
                  if (desc.includes('update') || desc.includes('modify')) return 'Update';
                  if (desc.includes('delete') || desc.includes('remove')) return 'Deletion';
                  if (desc.includes('export') || desc.includes('download')) return 'Export';
                  if (desc.includes('import') || desc.includes('upload')) return 'Import';
                  if (desc.includes('view') || desc.includes('access')) return 'Access';
                  return 'General';
                };

                const getActivityIcon = (type: string) => {
                  switch (type) {
                    case 'Authentication': return '🔐';
                    case 'Creation': return '➕';
                    case 'Update': return '✏️';
                    case 'Deletion': return '🗑️';
                    case 'Export': return '📤';
                    case 'Import': return '📥';
                    case 'Access': return '👁️';
                    default: return '📋';
                  }
                };

                const activityType = getActivityType(activity.description || '');
                const activityIcon = getActivityIcon(activityType);

                return (
                  <TableRow 
                    key={index}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => activity.username && activity.organization && navigate(`/users/${activity.organization}/${activity.username}/activities`)}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Person sx={{ mr: 1, color: 'text.secondary' }} />
                        <Button
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            activity.username && activity.organization && navigate(`/users/${activity.organization}/${activity.username}/activities`);
                          }}
                          sx={{ 
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            p: 0,
                            minWidth: 'auto',
                            textAlign: 'left',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {activity.username || 'Unknown User'}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Typography sx={{ mr: 1, fontSize: '1.2em' }}>
                          {activityIcon}
                        </Typography>
                        <Chip
                          label={activityType}
                          size="small"
                          color={
                            activityType === 'Authentication' ? 'success' :
                            activityType === 'Creation' ? 'primary' :
                            activityType === 'Update' ? 'warning' :
                            activityType === 'Deletion' ? 'error' :
                            activityType === 'Export' ? 'info' :
                            activityType === 'Import' ? 'secondary' :
                            'default'
                          }
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {activity.description || 'No description available'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {activity.timestamp 
                          ? new Date(activity.timestamp).toLocaleString()
                          : 'Unknown time'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          activity.username && navigate(`/users/${activity.username}/activities`);
                        }}
                        title="View User Activities"
                        disabled={!activity.username}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

    </Box>
  );
};

export default OrganizationDetails;
